import React from 'react';
import { Box, Chip, CircularProgress, Divider, Tooltip } from '@mui/material';
import NotificationImportantIcon from '@mui/icons-material/NotificationImportant';

import { useEmailTemplateQuery, NotificationSettingsPageQuery } from 'generated/graphql';
import { Modal, ModalContents, ModalTypography } from 'components/Modal/Modal';
import { gray3 } from 'utils/color-styles';

import { NotificationMessage } from './NotificationSettingsForm';

interface EmailTemplateModalProps {
  message: NotificationMessage;
  categories: NonNullable<NotificationSettingsPageQuery['notificationMetadata']>['categories'];
  onClose: () => void;
}

export const EmailTemplateModal: React.FC<EmailTemplateModalProps> = ({
  message,
  categories,
  onClose,
}) => {
  const { data, loading, error } = useEmailTemplateQuery({
    variables: {
      id: message?.id ?? '',
    },
    skip: !message,
  });

  if (!message) {
    return null;
  }

  return (
    <Modal open onClose={onClose}>
      <ModalContents
        onClickClose={onClose}
        body={
          <Box>
            <Box
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <p style={{ fontSize: 16, fontWeight: 500, margin: 0 }}>{message.label}</p>
              {message.unignorable && (
                <Tooltip title="This notification is required">
                  <NotificationImportantIcon fontSize="large" color="primary" />
                </Tooltip>
              )}
            </Box>
            <Chip
              label={categories.find((category) => category?.id === message.category)?.label}
              size="small"
              variant="outlined"
              style={{ marginTop: 12 }}
            />

            <Divider style={{ marginTop: 16 }} />

            <Box>
              {loading && (
                <Box display="flex" justifyContent="center" p={4} style={{ height: 400 }}>
                  <CircularProgress />
                </Box>
              )}

              {error && (
                <Box p={4}>
                  <ModalTypography color="error">
                    Error loading email template: {error.message}
                  </ModalTypography>
                </Box>
              )}

              {data?.emailTemplate && (
                <>
                  <Box>
                    <p style={{ fontSize: 16, fontWeight: 400, marginTop: 20, marginBottom: 20 }}>
                      <span style={{ color: gray3 }}>Subject: </span> {data.emailTemplate.subject}
                    </p>
                  </Box>

                  <div dangerouslySetInnerHTML={{ __html: data.emailTemplate.bodyHtml }} />
                </>
              )}
            </Box>
          </Box>
        }
      />
    </Modal>
  );
};
